import { createSignal, onMount, onCleanup, Show, createEffect } from "solid-js";
import { SolidQR } from "solid-qr";
import { View, Text } from "@lightningtv/solid";
import { fontFamily, isRTL, t } from "../translations/configuration";

const QrCodeWithAxios = (props) => {
  const URL = `https://orbixplay.com/pricing?client_reference_id=${props.deviceID}`;

  const [blobUrl, setBlobUrl] = createSignal("");
  const [isLoading, setIsLoading] = createSignal(true);
  const [retryCount, setRetryCount] = createSignal(0);
  const MAX_RETRIES = 3;
  let qrImgRef;
  let intervalId;
  let timeoutId;

  const generateQRCode = () => {
    // Clear any existing intervals/timeouts
    if (intervalId) clearInterval(intervalId);
    if (timeoutId) clearTimeout(timeoutId);

    let attempts = 0;
    const MAX_ATTEMPTS = 50; // 5 seconds

    intervalId = setInterval(() => {
      attempts++;

      if (qrImgRef?.src?.startsWith("data:image")) {
        try {
          const base64 = qrImgRef.src;
          const url = base64ToBlobUrl(base64);
          setBlobUrl(url);
          setIsLoading(false);
          console.log('QR code generated successfully');
          clearInterval(intervalId);
          if (timeoutId) clearTimeout(timeoutId);
        } catch (error) {
          console.error('Error processing QR code:', error);
          handleFailure();
        }
      } else if (attempts >= MAX_ATTEMPTS) {
        console.warn('QR code generation timeout after', attempts, 'attempts');
        clearInterval(intervalId);
        handleFailure();
      }
    }, 100);

    // Safety timeout
    timeoutId = setTimeout(() => {
      if (isLoading()) {
        console.warn('QR code generation timeout (5s)');
        clearInterval(intervalId);
        handleFailure();
      }
    }, 5000);
  };

  const handleFailure = () => {
    if (retryCount() < MAX_RETRIES) {
      const newRetryCount = retryCount() + 1;
      setRetryCount(newRetryCount);
      console.log(`Retrying QR generation... Attempt ${newRetryCount}/${MAX_RETRIES}`);

      // Exponential backoff: 1s, 2s, 3s
      setTimeout(() => {
        setIsLoading(true);
        setBlobUrl(""); // Clear previous attempt
        generateQRCode();
      }, 1000 * newRetryCount);
    } else {
      console.error('Max retries reached for QR generation');
      setIsLoading(false);
    }
  };

  onMount(() => {
    if (props.api_token) {
      generateQRCode();
    } else {
      console.error('No API token provided');
      setIsLoading(false);
    }
  });

  onCleanup(() => {
    if (intervalId) clearInterval(intervalId);
    if (timeoutId) clearTimeout(timeoutId);

    // Clean up blob URL to prevent memory leaks
    const url = blobUrl();
    if (url && url.startsWith('blob:')) {
      window.URL.revokeObjectURL(url);
    }
  });

  createEffect(() => {
    if (props.api_token) {
      setIsLoading(true);
      setBlobUrl("");
      setRetryCount(0);
      generateQRCode();
    }
  });

  function base64ToBlobUrl(base64) {
    try {
      const parts = base64.split(",");
      if (parts.length !== 2) {
        throw new Error('Invalid base64 format');
      }

      const byteString = atob(parts[1]);
      const mimeString = parts[0].split(":")[1].split(";")[0];

      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      const blob = new Blob([ab], { type: mimeString });
      return window.URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error creating blob URL:', error);
      throw error;
    }
  }

  return (
    <View width={350} height={350} x={-25} y={-25} style={{ fontSize: 20 }}>
      <Show when={isLoading()}>
        <Text style={{ fontSize: 20, color: "#ffffff" }}>
          Loading QR Code...
          {retryCount() > 0 && ` (Retry ${retryCount()}/${MAX_RETRIES})`}
        </Text>
      </Show>

      {/* Render SolidQR invisibly */}
      <div style={{ display: "none" }}>
        <SolidQR
          ref={qrImgRef}
          options={{
            type: "image/png",
            color: {
              dark: `#000000FF`,
              light: "#0000000"
            }
          }}
          text={URL}
        />
      </div>

      {/* Render QR code image */}
      <Show when={blobUrl() && !isLoading()}>
        <img
          src={blobUrl()}
          alt="QR Code"
          style={{ width: "400px", height: "400px" }}
          onError={() => {
            console.error('QR image failed to load');
            handleFailure();
          }}
        />
      </Show>

      {/* <View y={420} x={5}>
        <Text fontSize={27} x={isRTL() ? 1 : 5} y={14} color={"#ffffff"} style={{ fontFamily: fontFamily() }} fontWeight={isRTL() ? "normal" : "bold"}>{t('deviceId')}</Text>
        <View src={"https://i.ibb.co/WNPhwwTN/Arrow-Forward.png"} width={60} height={50} y={2} x={isRTL() ? 135 : 120} />
        <Text fontSize={30} x={isRTL() ? 180 : 170} y={14} color={"#448CD2"} style={{ fontFamily: fontFamily() }}>{props.deviceID}</Text>
      </View> */}

      {/* Error state after max retries */}
      <Show when={!isLoading() && !blobUrl() && retryCount() >= MAX_RETRIES}>
        <Text style={{ fontSize: 18 }}>
          Failed to load QR Code.{'\n'}Please refresh the page.
        </Text>
      </Show>
    </View>
  );
};

export default QrCodeWithAxios;